// @ts-strict-ignore
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { PersonaQuizBlockData } from 'api/cms';
import { PublicPage } from 'api/website';
import { Box } from 'components/Box';
import scrollIntoView from 'utils/scrollIntoView';

import { PersonaQuizBlockQuestion } from './PersonaQuizBlockQuestion';
import { PersonaQuizBlockResult } from './PersonaQuizBlockResult';

const uuid = () => Math.random().toString(36).slice(2);

type Props = {
  blockIndex: number;
  data: PersonaQuizBlockData;
  content?: PublicPage;
};

export function PersonaQuizBlock({
  blockIndex,
  data: { questions, results },
  content,
}: Props) {
  const [score, setScore] = useState(Array(questions.length).fill([]));
  const [result, setResult] = useState(-1);
  const [currentQuestion, setCurrentQuestion] = useState(-1);
  const [version, setVersion] = useState(uuid());
  const resultRef = useRef(null);
  const currentQuestionRef = useRef(null);

  const restart = () => {
    setResult(-1);
    setCurrentQuestion(0);
    setScore(Array(questions.length).fill([]));
    setVersion(uuid());
  };

  const updateScore = (index: number, points: Array<number>) => {
    const newScore = [...score];
    newScore[index] = points;
    setScore(newScore);
  };

  const getResult = useCallback(
    (finalScore) => {
      const tally = results.map((res, resultIndex) =>
        finalScore.reduce((acc, question) => acc + question[resultIndex], 0)
      );
      const persona = tally.reduce(
        (acc, total, index) => {
          if (total > acc.count) {
            return {
              count: total,
              index,
            };
          }

          return acc;
        },
        {
          count: 0,
          index: 0,
        }
      );
      return persona.index;
    },
    [results]
  );
  useEffect(() => {
    if (!score.some((question) => question.length === 0)) {
      const finalResult = getResult(score);
      setResult(finalResult);
    } else if (score.some((question) => question.length > 0)) {
      const nextQuestion = score.findIndex((question) => question.length === 0);
      setCurrentQuestion(nextQuestion);
    }
  }, [currentQuestion, getResult, score]);
  useEffect(() => {
    if (currentQuestionRef && currentQuestionRef.current) {
      scrollIntoView(currentQuestionRef.current);
    }
  }, [currentQuestion]);
  useEffect(() => {
    if (resultRef && resultRef.current) {
      scrollIntoView(resultRef.current);
    }
  }, [result]);
  return (
    <Box data-qa-id="cms-persona-quiz" key={version}>
      {questions.map((question, index) => (
        <div
          ref={index === currentQuestion ? currentQuestionRef : null} // eslint-disable-next-line react/no-array-index-key
          key={index}
        >
          <PersonaQuizBlockQuestion
            blockIndex={blockIndex}
            question={question}
            index={index}
            updateScore={updateScore}
            answered={score[index].length > 0}
          />
        </div>
      ))}
      {result > -1 && (
        <Box mt={64}>
          <div ref={resultRef}>
            <PersonaQuizBlockResult
              blockIndex={blockIndex}
              result={results[result]}
              clickRetake={restart}
              content={content}
            />
          </div>
        </Box>
      )}
    </Box>
  );
}
